/**
 * AdminVideoStudioPage.jsx — admin UI for generating product videos.
 *
 * Routes here at /admin/video-studio.
 *
 * Capabilities (MVP):
 *   • Browse candidate products (HustleShop catalogue)
 *   • Trigger single-product generation with voice + orientation choice
 *   • Trigger batch generation (all / featured / by category)
 *   • Preview generated videos inline
 *   • View distribution config (all platforms OFF by default)
 *
 * Distribution toggles per platform are gated behind a "Shop is live"
 * global switch — flip when ready.
 */
import { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../../lib/api";
import { toast } from "sonner";

const VOICES = [
  { id: "nova",    label: "Nova — energetic, upbeat" },
  { id: "onyx",    label: "Onyx — deep, authoritative" },
  { id: "shimmer", label: "Shimmer — bright, cheerful" },
  { id: "alloy",   label: "Alloy — neutral, balanced" },
  { id: "fable",   label: "Fable — expressive, storytelling" },
];

const CATEGORIES = [
  "", "fishing", "camping", "marine", "home-garden",
  "kitchen", "electronics", "sports-fitness", "tools-diy", "lifestyle",
];

const PLATFORMS = [
  { id: "youtube",   label: "YouTube Shorts",     emoji: "▶️" },
  { id: "facebook",  label: "Facebook Reels",     emoji: "📘" },
  { id: "instagram", label: "Instagram Reels",    emoji: "📷" },
  { id: "tiktok",    label: "TikTok",             emoji: "🎵" },
  { id: "linkedin",  label: "LinkedIn",           emoji: "💼" },
  { id: "pinterest", label: "Pinterest",          emoji: "📌" },
];

export default function AdminVideoStudioPage() {
  const [products, setProducts] = useState([]);
  const [videos, setVideos]     = useState([]);
  const [stats, setStats]       = useState({ total: 0, ready: 0 });
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState({ category: "", featured_only: false });
  const [voice, setVoice]       = useState("nova");
  const [orientation, setOrientation] = useState("vertical");
  const [busy, setBusy]         = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);
  const [distConfig, setDistConfig] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const pu = new URL(`${API_BASE}/video-studio/products`, window.location.origin);
      if (filter.category) pu.searchParams.set("category", filter.category);
      if (filter.featured_only) pu.searchParams.set("featured_only", "true");
      const pr = await fetch(pu, { credentials: "include" });
      const pj = await pr.json();
      setProducts(pj.items || []);

      const vr = await fetch(`${API_BASE}/video-studio/videos?limit=200`,
                              { credentials: "include" });
      const vj = await vr.json();
      setVideos(vj.items || []);
      setStats({ total: vj.total || 0, ready: vj.ready || 0 });

      const dr = await fetch(`${API_BASE}/video-studio/distribution/config`,
                              { credentials: "include" });
      if (dr.ok) setDistConfig(await dr.json());
    } catch (e) {
      toast.error("Could not load — are you signed in as admin?");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter.category, filter.featured_only]);

  const generateOne = async (slug) => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/video-studio/generate/${slug}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orientation, voice, force: false }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e?.detail || `Generate ${slug} failed`);
        return;
      }
      const v = await r.json();
      toast.success(`Video ready — ${slug}`);
      setPreviewSrc(v.url);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const batchGenerate = async () => {
    if (!window.confirm(`Generate up to ${products.filter(p => !p.has_video).length} videos? Each takes ~10 seconds.`)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/video-studio/batch`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          only_featured: filter.featured_only,
          category: filter.category || null,
          orientation, voice, force: false, limit: 200,
        }),
      });
      const j = await r.json();
      if (r.ok) {
        toast.success(`${j.queued} videos queued in background — refresh in a few minutes`);
      } else {
        toast.error(j?.detail || "Batch failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const togglePlatform = async (platform, enabled) => {
    const r = await fetch(`${API_BASE}/video-studio/distribution/config`, {
      method: "PATCH", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ platform, enabled }),
    });
    if (r.ok) {
      toast.success(`${platform} → ${enabled ? "enabled" : "disabled"}`);
      const dr = await fetch(`${API_BASE}/video-studio/distribution/config`,
                              { credentials: "include" });
      setDistConfig(await dr.json());
    } else {
      toast.error("Toggle failed");
    }
  };

  const platformsCfg = distConfig?.platforms || {};
  const globalEnabled = distConfig?.global_enabled || false;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 px-4 py-8" data-testid="admin-video-studio">
      <div className="max-w-6xl mx-auto">
        <header className="mb-8 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">🎬 HustleShop Video Studio</h1>
            <p className="text-slate-400 text-sm mt-1">
              Generate product videos using Ken-Burns slideshow + AI voiceover. Cost ~$0.02/video.
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-amber-400" data-testid="videos-ready">{stats.ready}</p>
            <p className="text-xs text-slate-400">videos ready</p>
          </div>
        </header>

        {/* Generation controls */}
        <section className="border border-slate-800 rounded-xl p-5 mb-6 bg-slate-900/40">
          <h2 className="font-semibold mb-3">Render settings</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="block">
              <span className="text-xs uppercase text-slate-400">Voice</span>
              <select value={voice} onChange={(e) => setVoice(e.target.value)}
                       data-testid="voice-select"
                       className="w-full mt-1 bg-slate-950 border border-slate-700 rounded px-3 py-2">
                {VOICES.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-slate-400">Orientation</span>
              <select value={orientation} onChange={(e) => setOrientation(e.target.value)}
                       data-testid="orientation-select"
                       className="w-full mt-1 bg-slate-950 border border-slate-700 rounded px-3 py-2">
                <option value="vertical">Vertical 1080x1920 (Reels/Shorts/TikTok)</option>
                <option value="square">Square 1080x1080 (FB feed/Pinterest)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase text-slate-400">Category filter</span>
              <select value={filter.category}
                       onChange={(e) => setFilter({ ...filter, category: e.target.value })}
                       data-testid="category-filter"
                       className="w-full mt-1 bg-slate-950 border border-slate-700 rounded px-3 py-2">
                {CATEGORIES.map(c => <option key={c || "all"} value={c}>{c || "All categories"}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 pt-5">
              <input type="checkbox"
                       checked={filter.featured_only}
                       onChange={(e) => setFilter({ ...filter, featured_only: e.target.checked })}
                       data-testid="featured-toggle" />
              <span className="text-sm">Featured products only</span>
            </label>
          </div>
          <div className="mt-4 flex gap-2 flex-wrap">
            <button onClick={batchGenerate} disabled={busy}
                    data-testid="batch-generate"
                    className="bg-amber-400 text-slate-900 font-semibold px-4 py-2 rounded text-sm disabled:opacity-50">
              {busy ? "Queuing…" : `Batch generate ${products.filter(p => !p.has_video).length} pending`}
            </button>
            <button onClick={load}
                    className="bg-slate-800 hover:bg-slate-700 px-4 py-2 rounded text-sm">
              Refresh
            </button>
          </div>
        </section>

        {/* Distribution config */}
        <section className="border border-slate-800 rounded-xl p-5 mb-6 bg-slate-900/40">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <h2 className="font-semibold">📤 Distribution</h2>
            <span className={`text-xs px-2 py-1 rounded font-semibold ${globalEnabled ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-700 text-slate-300"}`}
                   data-testid="dist-status">
              {globalEnabled ? "LIVE" : "PAUSED (videos generated, nothing posted yet)"}
            </span>
          </div>
          <p className="text-xs text-slate-400 mb-3">
            Toggle each platform once shop is live. Posting also requires OAuth tokens per platform — connect those before flipping the global switch.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {PLATFORMS.map(p => {
              const cfg = platformsCfg[p.id] || {};
              const enabled = !!cfg.enabled;
              return (
                <div key={p.id} className="border border-slate-800 rounded p-3 bg-slate-950/40"
                     data-testid={`platform-${p.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold"><span aria-hidden>{p.emoji}</span> {p.label}</span>
                    <button
                      onClick={() => togglePlatform(p.id, !enabled)}
                      data-testid={`platform-toggle-${p.id}`}
                      className={`text-xs px-2 py-1 rounded ${enabled ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-800 hover:bg-slate-700"}`}>
                      {enabled ? "enabled" : "disabled"}
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    {enabled ? "Will post on schedule when global switch is ON" : "Skipped"}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Products grid */}
        {loading && <p className="text-slate-400">Loading…</p>}
        {!loading && (
          <section className="border border-slate-800 rounded-xl p-5 mb-6 bg-slate-900/20">
            <h2 className="font-semibold mb-3">{products.length} candidate products</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="text-left px-2 py-2">Product</th>
                    <th className="text-left px-2 py-2">Category</th>
                    <th className="text-left px-2 py-2">Status</th>
                    <th className="text-right px-2 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {products.slice(0, 100).map(p => (
                    <tr key={p.slug} className="border-t border-slate-800"
                        data-testid={`product-row-${p.slug}`}>
                      <td className="px-2 py-2 font-semibold">{p.title}</td>
                      <td className="px-2 py-2 text-xs text-slate-400">{p.category}</td>
                      <td className="px-2 py-2 text-xs">
                        {p.has_video
                          ? <span className="text-emerald-400">{p.videos.join(", ")}</span>
                          : <span className="text-slate-500">no videos yet</span>}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <button
                          onClick={() => generateOne(p.slug)}
                          disabled={busy}
                          data-testid={`generate-${p.slug}`}
                          className="text-xs px-2 py-1 rounded bg-amber-400 text-slate-900 font-semibold disabled:opacity-50">
                          Render
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Generated videos preview */}
        {videos.length > 0 && (
          <section className="border border-slate-800 rounded-xl p-5 bg-slate-900/20" data-testid="video-grid">
            <h2 className="font-semibold mb-3">Generated videos ({videos.length})</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {videos.slice(0, 24).map(v => (
                <div key={`${v.product_slug}-${v.orientation}`}
                     className="bg-slate-950/40 border border-slate-800 rounded p-2"
                     data-testid={`video-card-${v.product_slug}`}>
                  <video src={v.url} controls preload="metadata"
                         className="w-full aspect-[9/16] object-cover rounded bg-black" />
                  <p className="text-xs mt-2 line-clamp-2 font-semibold">{v.product_title}</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{v.orientation} · {Math.round(v.duration_s)}s</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Live preview modal */}
        {previewSrc && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
               onClick={() => setPreviewSrc(null)}>
            <video src={previewSrc} controls autoPlay
                   className="max-w-md max-h-[80vh] rounded shadow-2xl"
                   onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </div>
    </div>
  );
}
